/**
 * Admin → "Arviotilaukset" page. Lists smt_analysis_orders rows and lets the
 * admin approve / reject pending ones with an optional decision note.
 */
import { adminApproveOrder, adminListOrders, adminRejectOrder, type Order, type OrderStatus } from '../api/orders.ts';
import { currentUser } from '../state/session.ts';
import { t, getLang } from '../i18n/index.ts';
import { APIRequestError } from '../api/client.ts';

type Tab = OrderStatus | 'all';
const TABS: Tab[] = ['pending', 'approved', 'rejected', 'all'];
let activeTab: Tab = 'pending';

export async function renderAdminOrders(root: HTMLElement): Promise<void> {
  const u = currentUser();
  if (!u || u.role !== 'admin') {
    root.innerHTML = `<div class="simple-page"><div class="placeholder-card" style="color:rgb(140,55,55)">${escapeHtml(t('admin_only'))}</div></div>`;
    return;
  }

  root.innerHTML = `
    <div class="simple-page">
      <h1 class="simple-page-title">${escapeHtml(t('admin_orders_title'))}</h1>
      <div class="simple-page-sub">${escapeHtml(t('admin_orders_sub'))}</div>
      <div class="admin-tabs-bar" id="orderTabs">
        ${TABS.map(
          (tab) => `<button class="admin-tab-btn ${tab === activeTab ? 'active' : ''}" data-tab="${tab}">
            ${escapeHtml(t(`order_tab_${tab}`))}
          </button>`,
        ).join('')}
      </div>
      <div id="orderTable">${escapeHtml(t('view_admin_loading'))}</div>
    </div>
  `;

  root.querySelectorAll<HTMLButtonElement>('#orderTabs .admin-tab-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tab = btn.dataset.tab as Tab;
      if (!tab || tab === activeTab) return;
      activeTab = tab;
      void renderAdminOrders(root);
    });
  });

  await refreshTable(root);
}

async function refreshTable(root: HTMLElement): Promise<void> {
  const tbl = root.querySelector<HTMLDivElement>('#orderTable')!;
  tbl.textContent = t('view_admin_loading');
  try {
    const rows = await adminListOrders({ status: activeTab });
    if (!rows.length) {
      tbl.innerHTML = `<div class="placeholder-card">${escapeHtml(t('admin_orders_empty'))}</div>`;
      return;
    }
    tbl.innerHTML = `
      <div class="listing-table-wrap"><table class="listing-table">
        <thead><tr>
          <th>${escapeHtml(t('view_admin_th_created'))}</th>
          <th>${escapeHtml(t('my_estates_th_estate'))}</th>
          <th>${escapeHtml(t('admin_orders_th_requester'))}</th>
          <th>${escapeHtml(t('view_admin_th_status'))}</th>
          <th>${escapeHtml(t('my_estates_th_message'))}</th>
          <th>${escapeHtml(t('my_estates_th_decision_note'))}</th>
          <th>${escapeHtml(t('view_admin_th_actions'))}</th>
        </tr></thead>
        <tbody>
          ${rows.map(renderRow).join('')}
        </tbody>
      </table></div>
    `;

    tbl.querySelectorAll<HTMLButtonElement>('.btn-approve').forEach((btn) => {
      btn.addEventListener('click', () => decide(btn, 'approve', root));
    });
    tbl.querySelectorAll<HTMLButtonElement>('.btn-reject').forEach((btn) => {
      btn.addEventListener('click', () => decide(btn, 'reject', root));
    });
  } catch (e) {
    tbl.innerHTML = `<div class="placeholder-card" style="color:rgb(140,55,55)">${escapeHtml(formatErr(e as Error))}</div>`;
  }
}

function renderRow(o: Order): string {
  return `
    <tr>
      <td>${escapeHtml(formatDate(o.created_at))}</td>
      <td>${escapeHtml(o.estate_name || `#${o.estate_id}`)}<br><span style="opacity:.7">${escapeHtml(o.municipality || '')}, ${escapeHtml(o.estate_region || '')}</span></td>
      <td>${escapeHtml(o.user_name || o.user_email || `#${o.user_id}`)}</td>
      <td><span class="user-status-pill user-status-${escapeHtml(o.status)}">${escapeHtml(t('order_status_' + o.status))}</span></td>
      <td>${escapeHtml(o.message || '')}</td>
      <td>${escapeHtml(o.decision_note || '')}</td>
      <td>
        ${
          o.status === 'pending'
            ? `<button class="btn-approve" data-id="${o.id}">${escapeHtml(t('admin_action_approve'))}</button>
               <button class="btn-reject" data-id="${o.id}">${escapeHtml(t('admin_action_reject'))}</button>`
            : ''
        }
      </td>
    </tr>`;
}

async function decide(btn: HTMLButtonElement, action: 'approve' | 'reject', root: HTMLElement): Promise<void> {
  const id = Number(btn.dataset.id);
  const note = prompt(t('admin_orders_note_prompt')) ?? '';
  btn.disabled = true;
  try {
    if (action === 'approve') await adminApproveOrder(id, note);
    else await adminRejectOrder(id, note);
    await refreshTable(root);
  } catch (e) {
    alert(formatErr(e as Error));
  } finally {
    btn.disabled = false;
  }
}

function formatDate(s: string | undefined | null): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString(getLang() === 'en' ? 'en-GB' : 'fi-FI');
  } catch {
    return s;
  }
}
function formatErr(e: Error): string {
  if (e instanceof APIRequestError) return e.body.error || e.message;
  return e.message;
}
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
